import { useState } from 'react'
import { supabase } from '../lib/supabaseClient'
import { useAuth } from '../lib/useAuth'
import M55Mark from '../components/M55Mark'

const A = {
  page: { minHeight: '100vh', background: 'radial-gradient(ellipse at 50% 10%, rgba(77,214,255,.08), transparent 60%), #0e1116', color: '#eaf2fb', fontFamily: "'Saira Condensed', 'Rajdhani', sans-serif", display: 'flex', alignItems: 'center', justifyContent: 'center' },
  display: { fontFamily: "'Orbitron', 'Rajdhani', sans-serif", letterSpacing: '0.12em', textTransform: 'uppercase' },
  panel: { background: 'linear-gradient(135deg, #2a2e36, #1a1d22)', border: '1px solid #4a5058', boxShadow: 'inset 0 1px 0 rgba(255,255,255,.06), inset 0 -1px 0 rgba(0,0,0,.4), 0 4px 12px rgba(0,0,0,.4)', padding: '32px 24px', width: '100%', maxWidth: '400px' },
  input: { width: '100%', background: 'rgba(77,214,255,.04)', color: '#eaf2fb', border: '1px solid rgba(77,214,255,.25)', padding: '12px 16px', fontFamily: "'Saira Condensed', sans-serif", fontSize: '15px', outline: 'none' },
  label: { color: '#9aa6b0', fontSize: '11px', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '4px', display: 'block' },
  btnPrimary: { width: '100%', fontFamily: "'Orbitron', sans-serif", fontSize: '14px', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', background: 'linear-gradient(135deg, #ff7a18, #d8500a)', color: '#fff5e8', border: 'none', padding: '14px 20px', cursor: 'pointer', boxShadow: '0 0 14px rgba(255,122,24,.5), inset 0 1px 0 rgba(255,255,255,.25)' },
  muted: { color: '#9aa6b0' },
  faded: { color: '#5a6470' },
  accent: { color: '#ff7a18' },
  holo: { color: '#4dd6ff' },
}

export default function Auth() {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [isSignUp, setIsSignUp] = useState(false)
  const [loading, setLoading] = useState(false)
  const [message, setMessage] = useState('')
  const { user } = useAuth()

  const handleAuth = async (e) => {
    e.preventDefault(); setLoading(true); setMessage('')
    if (isSignUp) {
      const { error } = await supabase.auth.signUp({ email, password })
      if (error) setMessage(error.message)
      else setMessage('Check your email to confirm your account.')
    } else {
      const { data, error } = await supabase.auth.signInWithPassword({ email, password })
      if (error) { setMessage(error.message) }
      else if (data.session) {
        const { data: profile } = await supabase.from('users').select('role, platform_role').eq('id', data.session.user.id).limit(1)
        const p = profile?.[0]
        const pendingCode = localStorage.getItem('pending_join_code')
        if (pendingCode) { localStorage.removeItem('pending_join_code'); window.location.href = `/join/${pendingCode}`; return }
        // Platform staff (platform_role or legacy users.role='m55_admin') or
        // legacy users.role='gm' land on the dashboard.
        if (p?.platform_role || p?.role === 'm55_admin' || p?.role === 'gm') {
          window.location.href = '/dashboard'; return
        }
        // Co-GM / event-GM detection — their global users.role is just
        // 'player', but a per-event event_role of gm/co_gm should still
        // land them on the dashboard. Mirrors the ViewSwitcher gate.
        // Bug 61 re-open — event_players.id only exists on dev. Selecting
        // user_id keeps the post-login GM/co_gm detection working in both
        // envs so co_gms land on /dashboard instead of /play.
        const { data: gmRows } = await supabase
          .from('event_players')
          .select('user_id')
          .eq('user_id', data.session.user.id)
          .in('event_role', ['gm', 'co_gm'])
          .limit(1)
        if (gmRows?.[0]) { window.location.href = '/dashboard' }
        else { window.location.href = '/play' }
      }
    }
    setLoading(false)
  }

  return (
    <div style={A.page}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Orbitron:wght@400;500;600;700&family=Saira+Condensed:wght@400;500;600;700&display=swap');`}</style>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%', maxWidth: 400 }}>
      <div style={A.panel}>
        <div style={{ textAlign: 'center', marginBottom: '24px' }}>
          <img src="/anubis-logo.png" alt="Anubis" style={{ display: 'block', margin: '0 auto 8px', height: 120, width: 'auto' }} />
          <p style={{ ...A.muted, fontSize: '13px' }}>
            {isSignUp ? 'Create your operator account' : 'Sign in to continue'}
          </p>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <div>
            <label style={A.label}>Email</label>
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@email.com" maxLength={100} required style={A.input} />
          </div>
          <div>
            <label style={A.label}>Password</label>
            <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" maxLength={72} required style={A.input} />
          </div>

          {message && (
            <p style={{ fontSize: '13px', textAlign: 'center', color: message.includes('Check') ? '#4dd6ff' : '#ff4136' }}>{message}</p>
          )}

          <button onClick={handleAuth} disabled={loading} style={{ ...A.btnPrimary, opacity: loading ? 0.5 : 1 }}>
            {loading ? 'LOADING...' : isSignUp ? 'CREATE ACCOUNT' : 'SIGN IN'}
          </button>
        </div>

        <p style={{ textAlign: 'center', marginTop: '20px', fontSize: '13px', ...A.muted }}>
          {isSignUp ? 'Already have an account?' : "Don't have an account?"}{' '}
          <button onClick={() => setIsSignUp(!isSignUp)} style={{ background: 'none', border: 'none', cursor: 'pointer', ...A.accent, fontWeight: 600, fontFamily: "'Saira Condensed', sans-serif", fontSize: '13px' }}>
            {isSignUp ? 'Sign In' : 'Sign Up'}
          </button>
        </p>
        {!isSignUp && (
          <p style={{ textAlign: 'center', marginTop: '8px' }}>
            <button onClick={() => window.location.href = '/forgot-password'} style={{ background: 'none', border: 'none', cursor: 'pointer', ...A.faded, fontSize: '12px' }}>
              Forgot password?
            </button>
          </p>
        )}
      </div>
      <M55Mark variant="footer" />
      </div>
    </div>
  )
}
